import React, { Dispatch, FC, SetStateAction, useCallback, useState } from 'react'
import OpenInNew from '@mui/icons-material/OpenInNew'
import Info from '@mui/icons-material/Info'
import { track } from 'utils/analyticsV2'
import { Calendar, VendorScreeningResponse, VendorSiteVisitSchedulerInnerStatus } from './types'
import { computeVendorScreeningSteps, useUpdateVendorSiteVisit } from './utils'
import SchedulerAssign from './SchedulerAssign'
import ScreeningStep from './ScreeningStep'
import FailureModal from './FailureModal'

type ScreeningProps = {
  calendar: Calendar
  setCalendar: Dispatch<SetStateAction<Calendar>>
  setInnerStatus: Dispatch<SetStateAction<VendorSiteVisitSchedulerInnerStatus>>
}

const Screening: FC<ScreeningProps> = ({ calendar, setCalendar, setInnerStatus }) => {
  const [vendorScreeningResponses, setVendorScreeningResponses] = useState<VendorScreeningResponse[]>([])
  const [vendorScreeningStepIndex, setVendorScreeningStepIndex] = useState(0)

  const vendorScreeningSteps = computeVendorScreeningSteps(calendar)

  const { submitting, setDataToSubmit, error, clearError } = useUpdateVendorSiteVisit(
    calendar.id,
    vendorScreeningSteps.length,
    setCalendar,
    setInnerStatus,
    setVendorScreeningStepIndex
  )

  const step = vendorScreeningSteps[vendorScreeningStepIndex]

  const handleBack = useCallback(() => {
    track('click', {
      family: 'vendor-site-visit',
      code: 'back',
      feature: 'screening',
      flexfield_1: { stepTitle: step.q },
    })
    setVendorScreeningStepIndex((oldVendorScreeningStepIndex) => Math.max(0, oldVendorScreeningStepIndex - 1))
  }, [step.q])

  // NOTE: we're only showing partial address here, we don't want to share the full address yet
  // Not showing delivery_line_1 or delivery_line_2
  const address = [
    // calendar.property.delivery_line_1,
    // calendar.property.delivery_line_2,
    calendar.property.city,
    calendar.property.state,
    calendar.property.zip5,
  ]
    .filter(Boolean)
    .join(', ')

  const handleAddressClick = useCallback(() => {
    track('click', {
      family: 'vendor-site-visit',
      code: 'open',
      feature: 'address',
      content_text: address,
      flexfield_1: { stepTitle: step.q },
    })

    // Send iOS to apple maps. Everyone else goes to google.
    const url =
      /iPad|iPhone|iPod/.test(navigator.userAgent) && (window as any).MSStream
        ? `https://map.apple.com/?address=${encodeURIComponent(address)}`
        : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`
    window.open(url, '_blank')
  }, [address, step.q])

  return (
    <>
      {step.kind == 'assign' ? (
        <SchedulerAssign
          calendar={calendar}
          step={step}
          stepIndex={vendorScreeningStepIndex}
          submitting={submitting}
          setDataToSubmit={setDataToSubmit}
          vendorScreeningResponses={vendorScreeningResponses}
          setVendorScreeningResponses={setVendorScreeningResponses}
        >
          <div className="tw:bg-yellow-100 tw:text-yellow-1000 tw:text-sm tw:p-2 tw:flex tw:gap-1 tw:rounded-lg">
            <Info className="tw:!size-6 tw:text-yellow-600 tw:mt-1" />
            <div>
              {
                'Only accept projects you are willing and able to bid on. Late withdrawals, late bids, or unreasonable bids will result in fewer projects offered by Realm.'
              }
            </div>
          </div>
          <div>
            <div className="tw:text-sm tw:font-bold tw:text-tertiary-black">{'Address'}</div>
            <button
              type="button"
              onClick={handleAddressClick}
              className="tw:flex tw:items-center tw:hover:underline tw:mt-1"
            >
              <OpenInNew className="tw:!size-4" />
              <div>{address}</div>
            </button>
          </div>
          <div>
            <div className="tw:text-sm tw:font-bold tw:text-tertiary-black">{'Project scope'}</div>
            <div className="tw:mt-1" dangerouslySetInnerHTML={{ __html: calendar.property.scope || '' }} />
          </div>
        </SchedulerAssign>
      ) : (
        <ScreeningStep
          key={vendorScreeningStepIndex} // re-render on every step
          step={step}
          stepIndex={vendorScreeningStepIndex}
          submitting={submitting}
          setDataToSubmit={setDataToSubmit}
          vendorScreeningResponses={vendorScreeningResponses}
          setVendorScreeningResponses={setVendorScreeningResponses}
          onBack={handleBack}
        >
          {step.kind == 'docs' ? (
            <div>
              <div className="tw:text-sm tw:font-bold tw:text-tertiary-black">{'Plan documents'}</div>
              <div className="tw:flex tw:flex-col tw:gap-3 tw:items-start tw:text-sm tw:font-semibold tw:mt-2">
                {calendar.property.property_plan_files.map((e) => (
                  <a
                    key={e.url}
                    href={e.url}
                    target="_blank"
                    rel="noreferrer"
                    className="tw:flex tw:items-center tw:gap-1 tw:hover:underline"
                  >
                    <OpenInNew className="tw:!size-4" />
                    <div>{e.name}</div>
                  </a>
                ))}
              </div>
            </div>
          ) : step.kind == 'potential_project_challenges' ? (
            <div>
              <div className="tw:text-sm tw:font-bold tw:text-tertiary-black">{'Potential project challenges'}</div>
              <div
                className="tw:mt-1"
                dangerouslySetInnerHTML={{ __html: calendar.property.potential_project_challenges || '' }}
              />
            </div>
          ) : step.kind == 'additional_notes' ? (
            <div>
              <div className="tw:text-sm tw:font-bold tw:text-tertiary-black">{'Additional Notes'}</div>
              <div className="tw:mt-1" dangerouslySetInnerHTML={{ __html: calendar.property.additional_notes || '' }} />
            </div>
          ) : step.kind == 'deadlines' ? (
            <div className="tw:bg-yellow-100 tw:text-yellow-1000 tw:text-sm tw:p-2 tw:flex tw:gap-1 tw:rounded-lg">
              <Info className="tw:!size-6 tw:text-yellow-600 tw:mt-1" />
              <div>
                {
                  'Please respond accurately. Failure to meet the indicated deadline will result in fewer project opportunities provided by Realm.'
                }
              </div>
            </div>
          ) : null}
        </ScreeningStep>
      )}
      {error && <FailureModal title={error} onClose={clearError} />}
    </>
  )
}

export default Screening
